import * as React from 'react';
import { Link } from 'react-router-dom';

import { hashToCollection, QuestionForm, Spinner, PRODUCTION_FLAG, } from '../../index';

export interface ConceptData {
  displayName: string;
  id: number;
  level: number;
  name: string;
  parent_id: number | null;
  uid: string;
}

export interface ConceptsState {
  data: {
    [key: string]: ConceptData[];
  };
  hasreceiveddata: boolean;
}

export interface QuestionData {
  concept_uid?: string;
  conceptID?: string;
  flag?: string;
  key: string;
  prompt: string;
}

export interface QuestionsState {
  data: {
    [key: string]: any;
  };
}

export interface FillInBlankState {
  data: {
    [key: string]: any;
  };
}

export interface Match {
  params: {
    conceptID: string;
  };
}

interface ConceptProps {
  concepts: ConceptsState;
  containerClassName?: string;
  fillInBlank?: FillInBlankState;
  getQuestionLink?: (question: QuestionData) => string;
  includeDeleteButton?: boolean;
  includeQuestionForm?: boolean;
  isGrammarQuestion?: boolean;
  match: Match;
  onDeleteConcept?: (conceptID: string) => void;
  onSubmitNewQuestion?: (question: any, optimalResponse?: any) => void;
  questions: QuestionsState;
  renderLoading?: () => React.ReactNode;
  returnLink?: string;
  useConceptUid?: boolean;
}

export class Concept extends React.Component<ConceptProps> {
  getConcept = () => {
    const { concepts, match } = this.props;
    const { data } = concepts;
    const { params } = match;
    const { conceptID } = params;
    const dataRow = data['0'];

    if (!dataRow) {
      return null;
    }

    return dataRow.find((c) => c.uid === conceptID);
  };

  getQuestionsForConcept = () => {
    const { fillInBlank, match, questions, useConceptUid = false } = this.props;
    const { params } = match;
    const { conceptID } = params;

    let allQuestions = []

    if (questions) {
      const questionsCollection = hashToCollection(questions.data);
      allQuestions = questionsCollection;
    }

    if (fillInBlank) {
      const fillInBlankCollection = hashToCollection(fillInBlank.data);
      allQuestions = allQuestions.concat(fillInBlankCollection);
    }

    const conceptField = useConceptUid ? 'concept_uid' : 'conceptID';

    return allQuestions.filter(
      (question: QuestionData) =>
        question[conceptField] === conceptID && question.flag !== 'archived'
    );
  };

  handleDeleteConcept = () => {
    const { match, onDeleteConcept } = this.props;
    const { params } = match;
    const { conceptID } = params;

    if (onDeleteConcept && confirm('Are you sure?')) {
      onDeleteConcept(conceptID);
    }
  };

  handleSubmitNewQuestion = (question: any, optimalResponse?: any) => {
    const { onSubmitNewQuestion } = this.props;

    if (onSubmitNewQuestion) {
      onSubmitNewQuestion(question, optimalResponse);
    }
  };

  renderQuestionsForConcept = () => {
    const { fillInBlank, getQuestionLink } = this.props;
    const questionsForConcept = this.getQuestionsForConcept();
    const listItems = questionsForConcept.map((question: QuestionData, index: number) => {
      const flagTag =
        question.flag !== PRODUCTION_FLAG ? <strong>{question.flag.toUpperCase()} - </strong> : '';

      let linkPath: string;
      if (getQuestionLink) {
        linkPath = getQuestionLink(question);
      } else {
        const isFillInBlank = fillInBlank && question.key in fillInBlank.data;
        linkPath = isFillInBlank
          ? `/admin/fill-in-the-blanks/${question.key}/responses`
          : `/admin/questions/${question.key}/responses`;
      }

      return (
        <li className="question-box" key={question.key}>
          <Link to={linkPath}>
            <p>
              <span>{index + 1}.&nbsp;</span>
              {flagTag}
              <span dangerouslySetInnerHTML={{ __html: question.prompt }} />
            </p>
            {question.cues?.length ? (
              <div>
                {question.cues.map((cue, cueIndex) => (
                  <span className="cue" key={`cue-${question.key}-${cueIndex}`}>{cue}</span>
                ))}
              </div>
            ) : null}
          </Link>
        </li>
      );
    });
    return <ul>{listItems}</ul>;
  };

  renderNewQuestionForm = () => {
    const { concepts, includeQuestionForm, isGrammarQuestion = false, match } =
      this.props;
    const { params } = match;
    const { conceptID } = params;

    if (!includeQuestionForm) {
      return null;
    }

    return (
      <QuestionForm
        concepts={concepts}
        defaultConceptUID={conceptID}
        isGrammarQuestion={isGrammarQuestion}
        isNew={true}
        question={{}}
        submit={this.handleSubmitNewQuestion}
      />
    );
  };

  renderDeleteButton = () => {
    const { includeDeleteButton } = this.props;

    if (!includeDeleteButton) {
      return null;
    }

    return (
      <button
        className="button is-danger"
        onClick={this.handleDeleteConcept}
        type="button"
      >
        Delete Concept
      </button>
    );
  };

  render() {
    const {
      concepts,
      containerClassName = 'admin-container',
      renderLoading,
      returnLink = '/admin/concepts',
      questions,
    } = this.props;
    const { hasreceiveddata } = concepts;
    const concept = this.getConcept();

    if (hasreceiveddata === false) {
      if (renderLoading) {
        return renderLoading();
      }
      return <Spinner />;
    }

    if (concept) {
      return (
        <div className={`${containerClassName} tool-concepts`}>
          <Link to={returnLink}>Return to all {!questions ? 'Fill in Blank ' : ''}Concepts</Link>
          <h1 className="title">{concept.displayName}</h1>
          {this.getQuestionsForConcept().length === 0 && (
            <p className="subtitle">
              {this.getQuestionsForConcept().length} Questions
            </p>
          )}
          {this.renderDeleteButton()}
          {this.renderNewQuestionForm()}
          {this.renderQuestionsForConcept()}
        </div>
      );
    }

    return <p>404: No Concept Found</p>;
  }
}
